// src/pages/AdminBlogAnalytics.js
import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BlogContext } from '../BlogContext';
import AdminHeader from '../components/AdminHeader';
import { useAuth } from '../AuthContext';

const AdminBlogAnalytics = () => {
  const { id } = useParams();
  const { getPostById } = useContext(BlogContext);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
      return;
    }

    const postData = getPostById(id);
    if (postData) {
      setPost(postData);
    }

    // Check if we have real analytics data
    const hasRealData = postData?.analytics && postData.analytics.length > 0;

    if (hasRealData) {
      // Process real analytics data
      const analytics = postData.analytics;

      // Count unique visitors by IP
      const uniqueIPs = new Set(analytics.map(entry => entry.ip));
      const uniqueVisitors = uniqueIPs.size;

      // Process location data
      const locationMap = {};
      analytics.forEach(entry => {
        const country = entry.location?.country || 'Unknown';
        locationMap[country] = (locationMap[country] || 0) + 1;
      });

      const locations = Object.entries(locationMap)
        .map(([country, count]) => ({
          country,
          count,
          percentage: Math.round((count / analytics.length) * 100)
        }))
        .sort((a, b) => b.count - a.count);

      // Process device data
      const deviceMap = {};
      analytics.forEach(entry => {
        const device = entry.deviceInfo?.deviceType || 'Desktop';
        deviceMap[device] = (deviceMap[device] || 0) + 1;
      });

      const devices = Object.entries(deviceMap)
        .map(([type, count]) => ({
          type,
          count,
          percentage: Math.round((count / analytics.length) * 100)
        }))
        .sort((a, b) => b.count - a.count);

      // Process browser data
      const browserMap = {};
      analytics.forEach(entry => {
        const browser = entry.deviceInfo?.browser || 'Unknown';
        browserMap[browser] = (browserMap[browser] || 0) + 1;
      });

      const browsers = Object.entries(browserMap)
        .map(([name, count]) => ({
          name,
          count,
          percentage: Math.round((count / analytics.length) * 100)
        }))
        .sort((a, b) => b.count - a.count);

      // Process OS data
      const osMap = {};
      analytics.forEach(entry => {
        const os = entry.deviceInfo?.os || 'Unknown';
        osMap[os] = (osMap[os] || 0) + 1;
      });

      const operatingSystems = Object.entries(osMap)
        .map(([name, count]) => ({
          name,
          count,
          percentage: Math.round((count / analytics.length) * 100)
        }))
        .sort((a, b) => b.count - a.count);

      // Process referrer data
      const referrerMap = {};
      analytics.forEach(entry => {
        const referrer = entry.referrer || 'Direct';
        referrerMap[referrer] = (referrerMap[referrer] || 0) + 1;
      });

      const referrers = Object.entries(referrerMap)
        .map(([source, count]) => ({
          source,
          count,
          percentage: Math.round((count / analytics.length) * 100)
        }))
        .sort((a, b) => b.count - a.count);

      // Create timeline data (group by date)
      const timelineMap = {};
      analytics.forEach(entry => {
        const date = new Date(entry.timestamp).toLocaleDateString();
        timelineMap[date] = (timelineMap[date] || 0) + 1;
      });

      const timeline = Object.entries(timelineMap)
        .map(([date, views]) => ({ date, views }))
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      setAnalytics({
        totalViews: postData?.views || 0,
        uniqueVisitors,
        hasRealData: true,
        demographics: { locations, devices, browsers, operatingSystems },
        timeline,
        referrers
      });
    } else {
      // No real data available
      setAnalytics({
        totalViews: postData?.views || 0,
        hasRealData: false
      });
    }

    setLoading(false);
  }, [id, getPostById, isAuthenticated, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d0d0d] text-white">
        <AdminHeader />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="mt-2">Loading analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#0d0d0d] text-white">
        <AdminHeader />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h1 className="text-2xl font-bold">Post not found</h1>
            <button
              onClick={() => navigate('/admin/blog-stats')}
              className="mt-4 px-4 py-2 bg-blue-600 rounded hover:bg-blue-700"
            >
              Back to Stats
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white admin-page">
      <AdminHeader />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin/blog-stats')}
            className="mb-4 text-blue-500 hover:text-blue-400 flex items-center"
          >
            ← Back to Blog Stats
          </button>
          <h1 className="text-3xl font-bold mb-2">{post.title}</h1>
          <p className="text-gray-400">Detailed Analytics & Demographics</p>
        </div>

        {analytics.hasRealData ? (
          <>
            {/* Overview Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-blue-600 rounded-lg">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-400">Total Views</p>
                    <p className="text-2xl font-bold">{analytics.totalViews}</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-green-600 rounded-lg">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 3.5a1.5 1.5 0 013 0V4a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-.5a1.5 1.5 0 00-2.993.124l-2.5 8.5a1.5 1.5 0 01-2.993-.124l-.507-1.697a1.5 1.5 0 00-2.993-.124L4.5 14.5a1.5 1.5 0 01-2.993-.124l.507-1.697a1.5 1.5 0 00-2.993-.124L1.5 12.5A1.5 1.5 0 013.5 12l.507-1.697A1.5 1.5 0 006 10.5V9a1 1 0 001-1V6a1 1 0 011-1h3a1 1 0 001-1V3.5z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-400">Unique Visitors</p>
                    <p className="text-2xl font-bold">{analytics.uniqueVisitors}</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-purple-600 rounded-lg">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-400">Avg Views/Day</p>
                    <p className="text-2xl font-bold">{analytics.timeline.length > 0 ? Math.round(analytics.totalViews / analytics.timeline.length) : 0}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Location Chart */}
              <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4">Visitor Locations</h3>
                <div className="space-y-3">
                  {analytics.demographics.locations.map((location, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                        <span className="text-sm">{location.country}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="flex-1 bg-[#2a2a2a] rounded-full h-2 max-w-20">
                          <div
                            className="bg-blue-500 h-2 rounded-full"
                            style={{ width: `${location.percentage}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-400 w-12">{location.count}</span>
                        <span className="text-xs text-gray-500 w-8">({location.percentage}%)</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Device Chart */}
              <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4">Device Types</h3>
                <div className="space-y-3">
                  {analytics.demographics.devices.map((device, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-sm">{device.type}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="flex-1 bg-[#2a2a2a] rounded-full h-2 max-w-20">
                          <div
                            className="bg-green-500 h-2 rounded-full"
                            style={{ width: `${device.percentage}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-400 w-12">{device.count}</span>
                        <span className="text-xs text-gray-500 w-8">({device.percentage}%)</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Browser Chart */}
              <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4">Browser Usage</h3>
                <div className="space-y-3">
                  {analytics.demographics.browsers.map((browser, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                        <span className="text-sm">{browser.name}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="flex-1 bg-[#2a2a2a] rounded-full h-2 max-w-20">
                          <div
                            className="bg-purple-500 h-2 rounded-full"
                            style={{ width: `${browser.percentage}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-400 w-12">{browser.count}</span>
                        <span className="text-xs text-gray-500 w-8">({browser.percentage}%)</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* OS Chart */}
              <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4">Operating Systems</h3>
                <div className="space-y-3">
                  {analytics.demographics.operatingSystems.map((os, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                        <span className="text-sm">{os.name}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="flex-1 bg-[#2a2a2a] rounded-full h-2 max-w-20">
                          <div
                            className="bg-yellow-500 h-2 rounded-full"
                            style={{ width: `${os.percentage}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-400 w-12">{os.count}</span>
                        <span className="text-xs text-gray-500 w-8">({os.percentage}%)</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Timeline Chart */}
            <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6 mb-8">
              <h3 className="text-xl font-bold mb-4">Views Over Time (Last 7 Days)</h3>
              <div className="flex items-end space-x-2 h-32">
                {analytics.timeline.map((day, index) => (
                  <div key={index} className="flex-1 flex flex-col items-center">
                    <div
                      className="w-full bg-blue-500 rounded-t"
                      style={{ height: `${(day.views / Math.max(...analytics.timeline.map(d => d.views))) * 100}%` }}
                    ></div>
                    <span className="text-xs text-gray-400 mt-2">{day.date.split('/')[1]}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Referrers */}
            <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
              <h3 className="text-xl font-bold mb-4">Traffic Sources</h3>
              <div className="space-y-3">
                {analytics.referrers.map((referrer, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                      <span className="text-sm">{referrer.source}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="flex-1 bg-[#2a2a2a] rounded-full h-2 max-w-20">
                        <div
                          className="bg-orange-500 h-2 rounded-full"
                          style={{ width: `${referrer.percentage}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-400 w-12">{referrer.count}</span>
                      <span className="text-xs text-gray-500 w-8">({referrer.percentage}%)</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          /* No Data Available Message */
          <div className="text-center py-16">
            <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-8 max-w-md mx-auto">
              <div className="w-16 h-16 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">No Analytics Data Yet</h3>
              <p className="text-gray-400 text-sm mb-4">
                Analytics data will start appearing here as visitors view this blog post.
              </p>
              <div className="text-xs text-gray-500">
                <p>Current Views: {analytics.totalViews}</p>
                <p>Next visitor will trigger data collection</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBlogAnalytics;
